import * as React from 'react'
import { useState } from 'react'
import { useField } from 'remix-validated-form'
import { LockClosedIcon, EyeIcon, EyeSlashIcon } from '@heroicons/react/24/outline'

import { cn } from 'app/lib/utils'
import { Button } from 'app/components/ui/button'
import { FieldGroup, FieldLabel } from './field'
import { Input } from './input'

// `size` is omitted so it cannot shadow Input's variant scale with the DOM input's character width.
export interface FormPasswordProps extends Omit<React.ComponentProps<'input'>, 'onChange' | 'type' | 'size'> {
  name: string
  label?: string
  cols?: 1 | 2 | 3 | 4 | 5 | 6
  className?: string
  wrapperClassName?: string
  labelClassName?: string
  errorClassName?: string
  showErrorIcon?: boolean
  newLineError?: boolean
  error?: string
  onChange?: (e: React.ChangeEvent<HTMLInputElement>) => void
}

function FormPassword({
  name,
  label,
  placeholder,
  cols = 1,
  className,
  wrapperClassName,
  labelClassName,
  errorClassName,
  newLineError = true,
  error,
  showErrorIcon: _showErrorIcon,
  onChange,
  ...props
}: FormPasswordProps) {
  const [visibility, setVisibility] = useState(false)
  const { error: fieldError, getInputProps, setTouched } = useField(name)

  const displayError = fieldError ?? error
  const inputType = visibility ? 'text' : 'password'

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const inputProps = getInputProps({
    ...props,
    id: name,
    type: inputType,
    placeholder,
    onChange: (e: React.ChangeEvent<HTMLInputElement>) => {
      onChange?.(e)
      setTouched(true)
    },
    required: undefined,
  } as any)

  return (
    <FieldGroup className={cn(`col-span-12 md:col-span-${cols}`, wrapperClassName)}>
      {label && (
        <FieldLabel htmlFor={name} className={cn(labelClassName, displayError && 'text-red-600')}>
          {label}
        </FieldLabel>
      )}
      <Input
        {...(inputProps as React.ComponentProps<typeof Input>)}
        size="lg"
        wrapperClassName={className}
        icon={<LockClosedIcon className="h-5 w-5 text-strong" />}
        trailing={
          <Button
            type="button"
            variant="icon"
            size="icon-sm"
            onClick={() => setVisibility((v) => !v)}
            aria-label={visibility ? 'Hide password' : 'Show password'}
          >
            {visibility ? <EyeSlashIcon className="size-5 text-strong" /> : <EyeIcon className="size-5 text-strong" />}
          </Button>
        }
      />
      {displayError && newLineError && (
        <div className={cn(errorClassName, 'mt-2.25 text-body-sm text-red-600')}>{displayError}</div>
      )}
    </FieldGroup>
  )
}

export { FormPassword }
